import React, { useEffect, useMemo, useState } from 'react';

import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Dropdown,
  Flex,
  Form,
  GetProp,
  MenuProps,
  Select,
  Table,
  TablePaginationConfig,
  TableProps,
  message,
} from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Layout from '../../../components/Layout/Layout';
import { ApiErrorData } from '../../../models/ApiResponse';
import { ItemInterface } from '../../../models/ItemInterface';
import { ServerSideDataSchema } from '../../../models/ServerSideDataSchema';
import { User } from '../../../models/User';
import { TitaskIntegrationService } from '../../../services/TitaskIntegrationService';
import NewGeoVictoriaToBukDateConfigurationModal from './components/NewGeoVictoriaToBukDateConfigurationModal';

import './GeoVictoriaToBukDateConfiguration.scss';

export interface DateConfigurationRowSchema {
  id: number;
  executedAt: string;
  startDate: string;
  endDate: string;
  timezone: string;
  createdAt: Date;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

interface GeoVictoriaToBukDefaultConfigSchema {
  initDay: number;
  endDay: number;
  executeDay: number;
}

const startYear = 2023;
const currentYear = dayjs().year();
const PAGE_SIZE = 10;

function GeoVictoriaToBukDateConfiguration() {
  const { t } = useTranslation();
  const { configSettings }: User = useSelector((state: any) => state.auth);
  const dateFormat: string = useSelector(
    (s: any) => s.intl?.dateFormat || 'DD/MM/YYYY'
  );
  const geoVictoriaToBukDefaultData: GeoVictoriaToBukDefaultConfigSchema =
    JSON.parse(
      (
        configSettings.find((record) => {
          return record.key === 'GEOVICTORIA_BUK_DEFAULT_MIGRATION_TRIGGER';
        }) as any
      ).value
    );
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DateConfigurationRowSchema[]>(
    []
  );
  const months: ItemInterface[] = useMemo(
    () => [
      { value: 1, label: t('common.months.january') },
      { value: 2, label: t('common.months.february') },
      { value: 3, label: t('common.months.march') },
      { value: 4, label: t('common.months.april') },
      { value: 5, label: t('common.months.may') },
      { value: 6, label: t('common.months.june') },
      { value: 7, label: t('common.months.july') },
      { value: 8, label: t('common.months.august') },
      { value: 9, label: t('common.months.september') },
      { value: 10, label: t('common.months.october') },
      { value: 11, label: t('common.months.november') },
      { value: 12, label: t('common.months.december') },
    ],
    [t]
  );
  const years: ItemInterface[] = [];
  const [form] = Form.useForm();
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<
    number | undefined
  >();
  const [selectedYearFilter, setSelectedYearFilter] = useState<
    number | undefined
  >();

  for (let year = startYear; year <= currentYear; year++) {
    years.push({ value: year, label: year.toString() });
  }

  /* New Configuration */
  const [newConfigurationLoading, setNewConfigurationLoading] = useState(false);
  const [isNewConfigurationModalOpen, setIsNewConfigurationModalOpen] =
    useState(false);

  const getHelperText = (): React.ReactNode | string => {
    const currentMonth = dayjs().month() + 1;
    const currentYear = dayjs().year();
    const startDateRange = dayjs(
      `${currentYear}-${currentMonth}-${geoVictoriaToBukDefaultData.initDay}`
    ).format(dateFormat);
    const endDateRange = dayjs(
      `${currentYear}-${currentMonth}-${geoVictoriaToBukDefaultData.endDay}`
    ).format(dateFormat);
    const monthName = months.find((m) => m.value === currentMonth)?.label || '';
    return t(
      'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.helper_text',
      {
        executeDay: geoVictoriaToBukDefaultData.executeDay,
        monthName: monthName.toLowerCase(),
        currentYear,
        startDateRange,
        endDateRange,
      }
    );
  };

  const onFinish = (values: any) => {
    setSelectedMonthFilter(values.month);
    setSelectedYearFilter(values.year);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    selectedMonthFilter,
    selectedYearFilter,
  ]);

  const init = async () => {
    setLoading(true);

    await fetchData();

    setLoading(false);
  };

  const fetchData = async (currentPage?: number) => {
    setLoading(true);

    const getCustomGeoVictoriaMigrationTriggerResponse =
      await TitaskIntegrationService.getCustomGeoVictoriaMigrationTrigger({
        page: currentPage || tableParams.pagination?.current || 1,
        pageSize: tableParams.pagination?.pageSize || PAGE_SIZE,
        month: selectedMonthFilter,
        year: selectedYearFilter,
      });

    if (getCustomGeoVictoriaMigrationTriggerResponse.success) {
      const data =
        getCustomGeoVictoriaMigrationTriggerResponse.data as ServerSideDataSchema<DateConfigurationRowSchema>;
      setDataSource(data.data);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: parseInt(`${data.totalRowsFiltered}`),
        },
      });
    } else {
      const error =
        getCustomGeoVictoriaMigrationTriggerResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.errors.fetch_grid'
          ),
        3.5
      );
    }

    setLoading(false);
  };

  const handleTableChange: TableProps<DateConfigurationRowSchema>['onChange'] =
    (pagination, filters, sorter) => {
      setTableParams({
        pagination,
        filters,
        ...sorter,
      });

      if (pagination.pageSize !== tableParams.pagination?.pageSize) {
        setDataSource([]);
      }
    };

  const deleteConfiguration = async (row: DateConfigurationRowSchema) => {
    setLoading(true);
    const deleteResponse =
      await TitaskIntegrationService.deleteCustomGeoVictoriaMigrationTrigger(
        row.id
      );

    if (deleteResponse.success) {
      await fetchData(1);
      messageApi.success(
        t(
          'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.messages.delete_configuration_success'
        ),
        3.5
      );
    } else {
      const error = deleteResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.errors.delete_configuration_failed'
          ),
        3.5
      );
      setLoading(false);
    }
  };

  const getDropdownMenu = (
    row: DateConfigurationRowSchema
  ): MenuProps['items'] => {
    return [
      {
        key: '1',
        label: (
          <span
            onClick={() => {
              deleteConfiguration(row);
            }}
          >
            <DeleteOutlined />{' '}
            {t(
              'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.dropdown.delete_configuration'
            )}
          </span>
        ),
      },
    ];
  };

  const showNewConfigurationModal = () => {
    if (!loading) {
      setIsNewConfigurationModalOpen(true);
    }
  };

  const handleNewConfigurationModalOk = async (
    executedAt: string,
    startDate: string,
    endDate: string
  ) => {
    if (!newConfigurationLoading) {
      setNewConfigurationLoading(true);
      const createResponse =
        await TitaskIntegrationService.createCustomGeoVictoriaMigrationTrigger({
          executedAt,
          startDate,
          endDate,
        });

      if (createResponse.success) {
        setIsNewConfigurationModalOpen(false);
        messageApi.success(
          t(
            'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.messages.create_configuration_success'
          ),
          3.5
        );
      } else {
        const error = createResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.errors.create_configuration_failed'
            ),
          3.5
        );
        setLoading(false);
      }

      setNewConfigurationLoading(false);
      await fetchData(1);
    }
  };

  const handleNewConfigurationModalCancel = () => {
    if (!newConfigurationLoading) {
      setIsNewConfigurationModalOpen(false);
    }
  };

  return (
    <>
      {contextHolder}

      <Layout title={''}>
        <Alert message={getHelperText()} type="info" showIcon />

        <Card style={{ marginTop: '12px' }}>
          <Flex justify="space-between">
            <Form form={form} layout="inline" onFinish={onFinish}>
              <Form.Item
                name="month"
                label={t(
                  'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.filters.month'
                )}
              >
                <Select
                  style={{ width: '125px' }}
                  placeholder={t(
                    'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.filters.select_option'
                  )}
                  allowClear
                  disabled={loading}
                >
                  {months.map((month) => (
                    <Select.Option key={month.value} value={month.value}>
                      {month.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="year"
                label={t(
                  'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.filters.year'
                )}
              >
                <Select
                  style={{ width: '125px' }}
                  placeholder={t(
                    'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.filters.select_option'
                  )}
                  allowClear
                  disabled={loading}
                >
                  {years.map((year) => (
                    <Select.Option key={year.value} value={year.value}>
                      {year.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t(
                      'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.filters.filter'
                    )}
                  </Button>
                )}
              </Form.Item>
            </Form>

            <Button
              style={{ float: 'right' }}
              type="primary"
              loading={loading}
              onClick={() => {
                showNewConfigurationModal();
              }}
            >
              <PlusOutlined />{' '}
              {t(
                'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.buttons.new_configuration'
              )}
            </Button>
          </Flex>
        </Card>
        <Card style={{ marginTop: '12px' }}>
          <Table<DateConfigurationRowSchema>
            dataSource={dataSource}
            bordered
            loading={loading}
            size="small"
            scroll={{ x: 1300 }}
            pagination={tableParams.pagination}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.table.columns.executed_at'
              )}
              dataIndex="executedAt"
              key="executedAtFormatted"
              render={(executedAt: string) => (
                <span>
                  {dayjs(executedAt, 'YYYY-MM-DD').format(dateFormat)}
                </span>
              )}
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.table.columns.range'
              )}
              key="range"
              render={(record: DateConfigurationRowSchema) => (
                <span>{`${dayjs(record.startDate, 'YYYY-MM-DD').format(
                  dateFormat
                )} - ${dayjs(record.endDate, 'YYYY-MM-DD').format(
                  dateFormat
                )}`}</span>
              )}
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.table.columns.timezone'
              )}
              dataIndex="timezone"
              key="timezone"
            />
            <Column
              title={t(
                'scenes.geovictoria.geo_victoria_to_buk_date_configuration.scene.table.columns.created_at'
              )}
              dataIndex="createdAt"
              key="createdAtFormatted"
              render={(createdAt: Date) => (
                <span>{dayjs(createdAt).format(`${dateFormat} h:mm A`)}</span>
              )}
            />
            <Column
              width={120}
              align="center"
              title=""
              key="actions"
              render={(row) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>

        {/* Modals */}
        <NewGeoVictoriaToBukDateConfigurationModal
          isNewConfigurationModalOpen={isNewConfigurationModalOpen}
          handleNewConfigurationModalOk={handleNewConfigurationModalOk}
          handleNewConfigurationModalCancel={handleNewConfigurationModalCancel}
          loading={newConfigurationLoading}
        />
      </Layout>
    </>
  );
}

export default GeoVictoriaToBukDateConfiguration;
